import { useMemo, useState } from 'react';
import { Box } from '@cbhq/cds-web/layout/Box';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { Pagination } from '@cbhq/cds-web/pagination/Pagination';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@cbhq/cds-web/tables';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { Text } from '@cbhq/cds-web/typography/Text';
import { defineMessages, useIntl } from '@cbhq/i18n';
import type { CoinbaseEngUsersAccountManagementApiApiOrganizationDomainClaim } from '@cbhq/identity-api-hooks/account-management/types';
import { CoinbaseEngUsersAccountManagementApiApiOrganizationDomainClaimEnforcementState } from '@cbhq/identity-api-hooks/account-management/types';

const messages = defineMessages({
  tableName: {
    id: 'Security.claimADomain.tableName',
    defaultMessage: 'Claimed domains',
    description: 'Accessible label for the claimed domains table',
  },
  colDomain: {
    id: 'Security.claimADomain.colDomain',
    defaultMessage: 'Domain',
    description: 'Column header for the claimed domain',
  },
  colStatus: {
    id: 'Security.claimADomain.colStatus',
    defaultMessage: 'Status',
    description: 'Column header for the domain claim status',
  },
  colCreated: {
    id: 'Security.claimADomain.colCreated',
    defaultMessage: 'Created',
    description: 'Column header for when the domain claim was created',
  },
  statusGracePeriod: {
    id: 'Security.claimADomain.statusGracePeriod',
    defaultMessage: 'Grace period',
    description: 'Status label for a domain claim in its grace period',
  },
  statusEnforced: {
    id: 'Security.claimADomain.statusEnforced',
    defaultMessage: 'Enforced',
    description: 'Status label for a domain claim that has been enforced',
  },
  statusUnknown: {
    id: 'Security.claimADomain.statusUnknown',
    defaultMessage: 'Unknown',
    description: 'Status label for a domain claim with an unrecognized status',
  },
  previousPage: {
    id: 'Security.claimADomain.previousPage',
    defaultMessage: 'Previous page',
    description: 'Accessibility label for the previous page button',
  },
  nextPage: {
    id: 'Security.claimADomain.nextPage',
    defaultMessage: 'Next page',
    description: 'Accessibility label for the next page button',
  },
});

const ITEMS_PER_PAGE = 8;
const tableStyle: React.CSSProperties = { width: '100%', minWidth: 0 };
const tableCellWhitespaceStyle = { paddingY: 0, paddingX: 3 } as const;
const firstTableCellWhitespaceStyle = { paddingY: 0, paddingX: 0, paddingStart: 2 } as const;
// Matches the Team tab's row spacing (UserCell's outerSpacing), which uses
// paddingY: 0.5 on body cells versus paddingY: 0 on header cells.
const bodyTableCellWhitespaceStyle = { paddingY: 0.5, paddingX: 3 } as const;
const firstBodyTableCellWhitespaceStyle = { paddingY: 0.5, paddingX: 0, paddingStart: 2 } as const;
// Reserves height for a full page (header + 8 rows, ~65px per row) on the
// OUTER container (a plain div, not the <table> itself — a <table>'s
// min-height stretches its existing rows to fill the space instead of
// leaving blank room below them). The pager's marginTop: 'auto' then pushes
// it to the bottom of that reserved space, so it sits at a fixed position
// regardless of how many rows the current page actually has.
const DOMAIN_CLAIMS_TABLE_MIN_HEIGHT = 600;
const containerStyle: React.CSSProperties = { minHeight: DOMAIN_CLAIMS_TABLE_MIN_HEIGHT };
const paginationBoxStyle: React.CSSProperties = { marginTop: 'auto' };

const EnforcementState =
  CoinbaseEngUsersAccountManagementApiApiOrganizationDomainClaimEnforcementState;

const statusToColorScheme = {
  [EnforcementState.ENFORCEMENT_STATE_GRACE_PERIOD]: 'yellow',
  [EnforcementState.ENFORCEMENT_STATE_ENFORCED]: 'green',
  [EnforcementState.ENFORCEMENT_STATE_UNSPECIFIED]: 'gray',
} as const;

export type DomainClaimsTableProps = {
  domainClaims: CoinbaseEngUsersAccountManagementApiApiOrganizationDomainClaim[];
};

export function DomainClaimsTable({ domainClaims }: DomainClaimsTableProps) {
  const { formatMessage, formatDate } = useIntl();
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(domainClaims.length / ITEMS_PER_PAGE));
  const activePage = Math.min(currentPage, totalPages);

  const paginatedClaims = useMemo(
    () => domainClaims.slice((activePage - 1) * ITEMS_PER_PAGE, activePage * ITEMS_PER_PAGE),
    [activePage, domainClaims],
  );

  const paginationAccessibilityLabels = useMemo(
    () => ({
      previous: formatMessage(messages.previousPage),
      next: formatMessage(messages.nextPage),
    }),
    [formatMessage],
  );

  const statusToMessage = useMemo(
    () => ({
      [EnforcementState.ENFORCEMENT_STATE_GRACE_PERIOD]: formatMessage(messages.statusGracePeriod),
      [EnforcementState.ENFORCEMENT_STATE_ENFORCED]: formatMessage(messages.statusEnforced),
      [EnforcementState.ENFORCEMENT_STATE_UNSPECIFIED]: formatMessage(messages.statusUnknown),
    }),
    [formatMessage],
  );

  return (
    <VStack width="100%" style={containerStyle} data-testid="domain-claims-table">
      <Table
        accessibilityLabel={formatMessage(messages.tableName)}
        tableLayout="fixed"
        style={tableStyle}
      >
        <TableHeader>
          <TableRow disableHoverIndicator>
            <TableCell as="th" scope="col" outerSpacing={firstTableCellWhitespaceStyle}>
              <Text font="label1" color="fgMuted">
                {formatMessage(messages.colDomain)}
              </Text>
            </TableCell>
            <TableCell as="th" scope="col" outerSpacing={tableCellWhitespaceStyle}>
              <Text font="label1" color="fgMuted">
                {formatMessage(messages.colStatus)}
              </Text>
            </TableCell>
            <TableCell as="th" scope="col" outerSpacing={tableCellWhitespaceStyle}>
              <Text font="label1" color="fgMuted">
                {formatMessage(messages.colCreated)}
              </Text>
            </TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedClaims.map((claim) => {
            const enforcementState =
              claim.enforcementState ?? EnforcementState.ENFORCEMENT_STATE_UNSPECIFIED;

            return (
              <TableRow key={claim.domain} disableHoverIndicator>
                <TableCell outerSpacing={firstBodyTableCellWhitespaceStyle}>
                  <Text font="label1">{claim.domain}</Text>
                </TableCell>
                <TableCell outerSpacing={bodyTableCellWhitespaceStyle}>
                  <Tag
                    colorScheme={
                      statusToColorScheme[enforcementState] ??
                      statusToColorScheme[EnforcementState.ENFORCEMENT_STATE_UNSPECIFIED]
                    }
                  >
                    {statusToMessage[enforcementState] ??
                      statusToMessage[EnforcementState.ENFORCEMENT_STATE_UNSPECIFIED]}
                  </Tag>
                </TableCell>
                <TableCell outerSpacing={bodyTableCellWhitespaceStyle}>
                  <Text font="label2" color="fgMuted">
                    {claim.createdAt
                      ? formatDate(new Date(claim.createdAt), { dateStyle: 'medium' })
                      : ''}
                  </Text>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {totalPages > 1 ? (
        <Box justifyContent="center" style={paginationBoxStyle}>
          <Pagination
            totalPages={totalPages}
            activePage={activePage}
            onChange={setCurrentPage}
            accessibilityLabels={paginationAccessibilityLabels}
            width={450}
            justifyContent="space-between"
          />
        </Box>
      ) : null}
    </VStack>
  );
}
